import {
  chakra,
  Flex,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
  Text,
  HStack,
  Button,
  useDisclosure,
  Spacer,
} from '@chakra-ui/react'
import { usePCProject } from 'api/pc-projects'
import { useGetUsersByType } from 'api/project-details'
import { parseEditTicketFormValuesToPayload, parseOrderIntakeValuesFromServer, useUpdateTicket } from 'api/ticket'
import AddNewTransactionModal from 'features/project-details/transactions/add-transaction-modal'
import { TransactionsTable } from 'features/project-details/transactions/transactions-table'
import { useSubFormErrors } from 'features/update-project-details/hooks'
// import InvoiceAndPayments from 'features/update-project-details/invoice-and-payments'
import React, { useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BiAddToQueue, BiErrorCircle, BiUpload } from 'react-icons/bi'
import { useLocation } from 'react-router-dom'
import { OrderIntakeForm } from './order-intake-form'
import { VendorDocumentsTable } from 'features/project-details/documents/documents-table'
import { UploadDocumentModal } from 'features/project-details/documents/upload-document'
import ReceivableNotes from 'features/project-details/receivable-notes-tab'
import { useNoteMutation } from 'api/work-order'
import PropertyAndClientTab from 'features/property/property-and-client-tab'
import { OrderMisc } from './order-misc'

type Props = {
  projectId: any
  onCloseModal?: () => void
  isReceivable?: boolean
  isDisabled?: boolean
  isTicketInvoiced?: boolean
}

enum ReceivableModalTabIndexes {
  TRANSACTIONS = 0,
  TICKET_DETAILS = 1,
  PROPERTY_DETAILS = 2,
  INVOICING = 3,
  DOCUMENTS = 4,
  NOTE = 5,
}

export const TicketStatusOptions = [
  { value: 1020, label: 'NEW' },
  { value: 34, label: 'ACTIVE' },
  { value: 37, label: 'INACTIVE' },
  { value: 36, label: 'COMPLETED' },
  { value: 6200, label: 'CONTACT 1' },
  { value: 6201, label: 'CONTACT 2' },
  { value: 110, label: 'INVOICED' },
  { value: 111, label: 'REJECTED' },
  { value: 1021, label: 'SCHEDULED' },
  { value: 1022, label: 'ESTIMATED' },
  { value: 1023, label: 'APPROVED' },
  //{ value: 1024, label: 'AWAITING' },
  { value: 1024, label: 'SUBMITTED' },
  { value: 68, label: 'PAID' },
  { value: 114, label: 'PAST DUE' },
  { value: 35, label: 'CANCELLED' },
  { value: 6202, label: 'DRAFT' },
  { value: 220, label: 'DISPUTED' },
]
export const WorkOrderStatusOptions = [
  { value: 1020, label: 'NEW' }, // "Active code" 34
  { value: 37, label: 'INACTIVE' },
  { value: 36, label: 'COMPLETED' },
  { value: 110, label: 'INVOICED' },
  { value: 111, label: 'REJECTED' },
  { value: 1021, label: 'SCHEDULED' },
  { value: 1023, label: 'APPROVED' },
  { value: 1022, label: 'ESTIMATED' },
  { value: 1024, label: 'SUBMITTED' },
  { value: 68, label: 'PAID' },
  { value: 35, label: 'CANCELLED' },
  { value: 1025, label: 'REVISION' },
  { value: 92, label: 'DECLINED' },
]
export const OverideTicketStatusOptions = [
  { value: 220, label: 'DISPUTED' },
  { value: 35, label: 'CANCELLED' },
]

export const TicketTimeSlots = [
  { value: 1, label: '08:00-10:00 AM' },
  { value: 2, label: '10:00-12:00 PM' },
  { value: 3, label: '12:00-02:00 PM' },
  { value: 4, label: '02:00-04:00 PM' },
  { value: 5, label: '04:00-06:00 PM' },
]

export const OrderIntakeFormEdit: React.FC<Props> = (props): JSX.Element => {
  const { userSelectOptions: maintenanceCoordinatorSelectOptions } = useGetUsersByType(112)
  const { userSelectOptions: fpmAsMaintenanceSpecialistSelectOptions } = useGetUsersByType(5)
  const { mutate: updateTicket, isLoading } = useUpdateTicket()

  const toast = useToast()
  const { projectData, isRefetching } = usePCProject(props.projectId)
  const { mutate: createNotes } = useNoteMutation(projectData?.id)
  const formReturn = useForm<any>()
  const { handleSubmit, reset } = formReturn
  const [isNotesRequired, setIsNotesRequired] = useState(false)
  const {
    isOpen: isOpenTransactionModal,
    onClose: onTransactionModalClose,
    onOpen: onTransactionModalOpen,
  } = useDisclosure()

  React.useEffect(() => {
    if (!projectData) return
    parseOrderIntakeValuesFromServer(
      projectData,
      maintenanceCoordinatorSelectOptions,
      fpmAsMaintenanceSpecialistSelectOptions,
    )
    reset(
      parseOrderIntakeValuesFromServer(
        projectData,
        maintenanceCoordinatorSelectOptions,
        fpmAsMaintenanceSpecialistSelectOptions,
      ),
    )
  }, [projectData, maintenanceCoordinatorSelectOptions?.length, fpmAsMaintenanceSpecialistSelectOptions?.length])

  const sendNote = () => {
    const payload = {
      comment: message,
      projectId: projectData?.id,
    }
    setMessage('')
    createNotes(payload)
  }
  const onSubmit = async values => {
    const payload = (await parseEditTicketFormValuesToPayload(values, projectData)) as any
    payload.isReceivable = isReceivable
    updateTicket(payload, {
      onSuccess: () => {
        setIsNotesRequired(false)
        toast({
          title: 'Order Intake',
          description: 'Ticket updated successfully',
          status: 'success',
          isClosable: true,
          position: 'top-left',
        })
        props.onCloseModal?.()
      },
      onError: (error: any) => {
        toast({
          title: 'Ticket',
          description: (error.title as string) ?? 'Unable to update ticket.',
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: 'top-left',
        })
      },
    })
  }
  const isReceivable = props?.isReceivable
  const isTicketInvoiced = props?.isTicketInvoiced
  const {
    formState: { errors },
  } = formReturn

  const { t } = useTranslation()
  const getTabIndex = useTicketTabsIndex(isReceivable as boolean)
  // HN-PSWOA-5863| Changing variable name from TabIndex to avoid variable name error
  const [tabIndex, setTabIndex] = useState(getTabIndex.INITIAL_TAB)
  const { isTicketManagementFormErrors } = useSubFormErrors(errors)
  const tabProps = { colorScheme: isReceivable ? 'brand' : 'brand', variant: isReceivable ? 'enclosed' : 'line' }
  const tabsContainerRef = useRef<HTMLDivElement>(null)
  const preventNewTransaction = !!(projectData?.projectStatus === 'paid' || projectData?.projectStatus === 'cancelled')
  const isProjectCancelled = projectData?.projectStatus === 'Cancelled'
  const [createdTransID, setCreatedTransID] = useState()
  const { isOpen: isOpenDocumentModal, onClose: onDocumentModalClose, onOpen: onDocumentModalOpen } = useDisclosure()

  const [message, setMessage] = useState<string>()

  const setCreatedTransaction = e => {
    setCreatedTransID(e?.data)
  }
  const location = useLocation()
  const transaction = (location?.state as any)?.transaction || {}

  React.useEffect(() => {
    if (isReceivable) setTabIndex(ReceivableModalTabIndexes.INVOICING) // HN-PSWOA-5863 -> Updated HN-PSWOA-6043| Set tab index to invoice for any type of ticket // // OLD:(Set tab index to invoice tab if clicked on invoiced ticket on AR grid)
  }, [isTicketInvoiced])

  return (
    <>
      <FormProvider {...formReturn}>
        <chakra.form onSubmit={handleSubmit(onSubmit)}>
          <Tabs
            onChange={i => setTabIndex(i)}
            pos="relative"
            top="-15px"
            index={tabIndex} // HN-PSWOA-5863| Set to controlled component to control the tab selection
            {...tabProps}
            // defaultIndex={isTicketInvoiced ? getTabIndex.INITIAL_TAB : 2}
          >
            <TabList {...(isReceivable ? { pos: 'relative', left: '-16px' } : {})}>
              {isReceivable && <TabCustom>Transactions</TabCustom>}
              <TabCustom isError={isTicketManagementFormErrors && tabIndex !== getTabIndex.TICKET_DETAILS}>
                Ticket Details
              </TabCustom>
              {isReceivable && <TabCustom>Property Details</TabCustom>}

              {!isReceivable && !isLoading && <TabCustom>Misc</TabCustom>}

              {isReceivable && <TabCustom>Documents</TabCustom>}
              {isReceivable && <TabCustom>Notes</TabCustom>}
            </TabList>
            {isReceivable && tabIndex === 4 && (
              <Flex justifyContent={'flex-end'} mt="10px">
                <Button colorScheme="brand" onClick={onDocumentModalOpen} leftIcon={<BiUpload />} mb="15px">
                  {t('projects.projectDetails.upload')}
                </Button>
              </Flex>
            )}
            <TabPanels>
              {isReceivable && (
                <TabPanel width="100%" p={0} pt={5} h="100%">
                  <HStack spacing="16px" mb="10px">
                    <Spacer />
                    <Button
                      variant="solid"
                      colorScheme="brand"
                      onClick={onTransactionModalOpen}
                      isDisabled={preventNewTransaction}
                      leftIcon={<BiAddToQueue />}
                    >
                      {t('projects.projectDetails.newTransaction')}
                    </Button>
                  </HStack>
                  <TransactionsTable
                    ref={tabsContainerRef}
                    projectStatus={projectData?.projectStatus as string}
                    defaultSelected={transaction}
                    transId={createdTransID}
                    projectId={props.projectId}
                    isReceivable={true}
                  />
                </TabPanel>
              )}
              <TabPanel
                width="100%"
                p={0}
                pt={5}
                overflowX={'auto'}
                sx={{
                  '&::-webkit-scrollbar': {
                    width: '10px',
                    height: '10px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#C2C7CD',
                    borderRadius: '1px',
                    border: '2px solid #f1f1f1',
                  },
                  scrollbarWidth: 'thin',
                }}
              >
                <OrderIntakeForm
                  isLoading={isLoading}
                  isNotesRequired={isNotesRequired}
                  setIsNotesRequired={setIsNotesRequired}
                  isReceivable={props.isReceivable}
                  maintenanceCoordinatorSelectOptions={maintenanceCoordinatorSelectOptions}
                  maintenanceSpecialistSelectOptions={fpmAsMaintenanceSpecialistSelectOptions}
                  isProjectCancelled={isProjectCancelled}
                  projectData={projectData}
                />
              </TabPanel>

              {isReceivable && (
                <TabPanel width="100%" p={0} pt={5} h="100%">
                  <PropertyAndClientTab propertyId={projectData?.propertyId?.toString()} />
                </TabPanel>
              )}

              <TabPanel>{!isRefetching && !isLoading && <OrderMisc ticketId={projectData?.id as number} />}</TabPanel>
              {isReceivable && (
                <TabPanel
                  width="100%"
                  p={0}
                  overflowY={'hidden'}
                  overflowX={'auto'}
                  sx={{
                    '&::-webkit-scrollbar': {
                      width: '10px',
                      height: '10px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#C2C7CD',
                      borderRadius: '1px',
                      border: '2px solid #f1f1f1',
                    },
                    scrollbarWidth: 'thin',
                  }}
                >
                  <VendorDocumentsTable
                    isReceivable={isReceivable}
                    ref={tabsContainerRef}
                    projectId={projectData?.id}
                  />
                </TabPanel>
              )}
              {isReceivable && (
                <TabPanel width="100%" p={0} minH={'260px'}>
                  <ReceivableNotes
                    sendNote={sendNote}
                    projectId={projectData?.id}
                    setMessage={setMessage}
                    message={message}
                  />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </chakra.form>
      </FormProvider>
      <UploadDocumentModal isOpen={isOpenDocumentModal} onClose={onDocumentModalClose} projectId={projectData?.id} />
      {isReceivable && (
        <AddNewTransactionModal
          isOpen={isOpenTransactionModal}
          onClose={onTransactionModalClose}
          setCreatedTransaction={setCreatedTransaction}
          projectId={props.projectId as string}
          projectStatus={projectData?.projectStatus as string}
        />
      )}
    </>
  )
}

const TabCustom: React.FC<{ isError?: boolean }> = ({ isError, children }) => {
  return (
    <Tab>
      {isError ? (
        <Flex alignItems="center">
          <Icon as={BiErrorCircle} size="18px" color="red.400" mr="1" />
          <Text color="red.400" fontSize="13px">
            {children}
          </Text>
        </Flex>
      ) : (
        <Text fontSize="13px">{children}</Text>
      )}
    </Tab>
  )
}

const useTicketTabsIndex = (isRecv: boolean) => {
  return {
    TRANSACTIONS: isRecv ? 0 : null,
    TICKET_DETAILS: isRecv ? 1 : 0,
    INVOICE_AND_PAYMENTS: isRecv ? 2 : 1,
    MISC: isRecv ? 3 : 2,
    INITIAL_TAB: isRecv ? 1 : 0,
  }
}
